import { ref, reactive, nextTick, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { GeneralTypeList } from "@/common/constant";
import {
  ListDeliveryTimeAllQuery,
  ListDeliveryTimeAllQueryVariables,
  ListDeliveryTimeAllDocument,
  GetDeliveryTimeAllCountQuery,
  GetDeliveryTimeAllCountQueryVariables,
  GetDeliveryTimeAllCountDocument,
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  GetDeliveryTimeQuery,
  GetDeliveryTimeQueryVariables,
  GetDeliveryTimeDocument,
  ListDeliveryGroupQuery,
  ListDeliveryGroupQueryVariables,
  ListDeliveryGroupDocument,
  DeliveryTimeCommonInput,
  DeliveryTimeDeleteInput,
  DeliveryGroupCreateInput,
  DeliveryGroupUpdateInput,
  DeliveryGroupDeleteInput,
  DeliveryTimeGetInput,
  DeliveryTimeListInput,
  DeliveryTimeAllListInput,
  DeliveryTimeAllCountInput,
  DeliveryGroupListInput,
  DeliveryGroupGetInput,
} from "@/generated/graphql";
import type { FormContext } from "vee-validate";
import type {
  ListData,
  ListGroupDeliveryId,
  ListDataAreaDeliveryIdResist,
  ListDeliveryTimeAllItem,
  ListGroupDeliveryIdItem,
  ChainSelectValue,
  ShopSelectValue,
  AreaSelectValue,
  DeliverySelectValue,
  DisplayDaysSettingValue,
  CapSelectValue,
  SelectTypeValue,
  DeliveryGroupSelectValue,
  DeliveryGroupListItem,
  AreaListItem,
} from "../types";
import { DELIVERY_ITEMS, DISPLAY_DAYS_SETTING_ITEMS, GROUP_DELIVERY_ITEMS } from "../constants";

interface UseDeliverySettingActionsParams {
  // UI state
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  processFlag: Ref<number>;
  mode: Ref<string>;
  displayInputDialog: Ref<boolean>;
  displayConfirmDialog: Ref<boolean>;
  displayCompletedDialog: Ref<boolean>;
  areaDeliveryIdResistDialog: Ref<boolean>;
  completeFlg: Ref<boolean>;
  timeFormRef: Ref<FormContext | null>;
  groupFormRef: Ref<FormContext | null>;

  // Filter state
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  areaSelectValues: Ref<AreaSelectValue>;
  areaEditSelectValues: Ref<AreaSelectValue>;
  areaListItems: Ref<AreaListItem[]>;
  newAreaListItems: Ref<AreaListItem[]>;
  shopListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null | undefined)[]>;
  deliveryGroupListItems: Ref<DeliveryGroupListItem[]>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  bufDeliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  listDataArray: Ref<ListData[]>;
  listGroupDeliveryIdDataArray: Ref<ListGroupDeliveryId[]>;
  mappedListData: Ref<Map<string, ListData[]>>;

  // Inputs
  deliveryTimeCommonInput: DeliveryTimeCommonInput;
  deliveryTimeDeleteInput: DeliveryTimeDeleteInput;
  deliveryGroupCreateInput: DeliveryGroupCreateInput;
  deliveryGroupUpdateInput: DeliveryGroupUpdateInput;
  deliveryGroupDeleteInput: DeliveryGroupDeleteInput;
  deliveryTimeGetInput: DeliveryTimeGetInput;
  deliveryTimeListInput: DeliveryTimeListInput;
  deliveryTimeAllListInput: DeliveryTimeAllListInput;
  deliveryGroupListInput: DeliveryGroupListInput;
  deliveryGroupGetInput: DeliveryGroupGetInput;

  // Form state
  deliverySelectValues: Ref<DeliverySelectValue>;
  displayDaysSettingValues: Ref<DisplayDaysSettingValue>;
  selectTimeCapSelectValues: Ref<CapSelectValue>;
  selectGroupCapSelectValues: Ref<CapSelectValue>;
  selectTypeSelectValues: Ref<SelectTypeValue>;
  selectListCheckbox: Ref<ListDataAreaDeliveryIdResist[]>;

  // List data
  listDeliveryTimeAll: Ref<ListDeliveryTimeAllItem[]>;
  listDataAreaDeliveryIdResistArray: Ref<ListDataAreaDeliveryIdResist[]>;
  listGroupDeliveryId: Ref<ListGroupDeliveryIdItem[]>;

  // ページネーション状態
  areaDeliveryCurrentPage: Ref<number>;
  areaDeliveryTotalCount: Ref<number>;
  isLoadingAreaDelivery: Ref<boolean>;

  // Functions
  queryDeliverytime: () => Promise<void>;
  onChangeShop: () => Promise<void>;
  onChangeArea: () => Promise<void>;
  onChangeGroup: () => Promise<void>;
  syncTimeFormValues: () => Promise<void>;
  syncGroupFormValues: () => Promise<void>;
  createDeliveryTime: () => Promise<void>;
  createDeliveryGroup: () => Promise<void>;
  chkDeliveryId: () => Promise<boolean>;
  chkDeliveryIdType: () => Promise<boolean | undefined>;
  chkGroupDeliveryId: () => Promise<boolean>;
  getDelivery: () => Promise<void>;
  getAreaName: (area_code: string) => string;
  getShopName: (shop_code: string) => string;
  getDeliveryIdName: (delivery_id: number) => string;

  // Shared mutable object
  use_list: { [key: string]: number };
  wordList: Record<string, string>;
}

/**
 * 画面で共有する状態を初期化して返却する composable。
 *
 * @returns 画面で利用するリアクティブな状態オブジェクト
 */
export const useDeliverySettingActionsState = () => {
  //選択された値を格納する変数（配達便）
  const deliverySelectValues = ref<DeliverySelectValue>({
    label: "１便",
    delivery_id: 1,
  });

  //選択された値を格納する変数（表示設定）
  const displayDaysSettingValues = ref<DisplayDaysSettingValue>({
    label: "当日",
    display_days_setting: 0,
  });

  //選択された値を格納する変数（新規/既存）
  const selectTypeItems = GeneralTypeList.new_or_exist;
  const selectTypeSelectValues = ref<SelectTypeValue>({
    index: "0",
    item: "新規",
  });

  //選択された値を格納する変数（キャップ数設定 - 配達時間）
  const selectTimeCapItems = GeneralTypeList.cap_setting;
  const selectTimeCapSelectValues = ref<CapSelectValue>({
    index: 0,
    item: "全曜日",
  });

  //選択された値を格納する変数（キャップ数設定 - 配達グループ）
  const selectGroupCapItems = GeneralTypeList.cap_setting;
  const selectGroupCapSelectValues = ref<CapSelectValue>({
    index: 0,
    item: "全曜日",
  });

  // チェックボックス選択値
  const selectListCheckbox = ref<ListDataAreaDeliveryIdResist[]>([]);

  // 配達時間全件
  const listDeliveryTimeAll = ref<ListDeliveryTimeAllItem[]>([]);

  // エリア・便追加一覧
  const listDataAreaDeliveryIdResistArray = ref<ListDataAreaDeliveryIdResist[]>([]);

  // ページネーション状態
  const areaDeliveryCurrentPage = ref<number>(1);
  const areaDeliveryTotalCount = ref<number>(0);
  const isLoadingAreaDelivery = ref<boolean>(false);

  // 配達グループ登録 input
  const deliveryGroupCreateInput = reactive<DeliveryGroupCreateInput>({
    customer_code: localStorageGetCustomerCode(),
    chain_code: "",
    group_code: "",
    name: "",
    group_delivery_id: 0,
    assigned_id_list: [],
    user_id: localStorageGetUserId(),
    max_deliverable_unit: null,
    group_cap_mon: null,
    group_cap_tue: null,
    group_cap_wed: null,
    group_cap_thu: null,
    group_cap_fri: null,
    group_cap_sat: null,
    group_cap_sun: null,
  });

  // 配達グループ編集 input
  const deliveryGroupUpdateInput = reactive<DeliveryGroupUpdateInput>({
    assigned_id_list: [],
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    group_code: "",
    group_delivery_id: 0,
    max_deliverable_unit: null,
    name: "",
    user_id: localStorageGetUserId(),
  });

  // 配達グループ削除 input
  const deliveryGroupDeleteInput = reactive<DeliveryGroupDeleteInput>({
    chain_code: "",
    customer_code: localStorageGetCustomerCode(),
    group_code: "",
    group_delivery_id: 0,
    user_id: localStorageGetUserId(),
  });

  // エリア・便重複管理
  const use_list: { [key: string]: number } = {};

  return {
    deliverySelectValues,
    displayDaysSettingValues,
    selectTypeItems,
    selectTypeSelectValues,
    selectTimeCapItems,
    selectTimeCapSelectValues,
    selectGroupCapItems,
    selectGroupCapSelectValues,
    selectListCheckbox,
    listDeliveryTimeAll,
    listDataAreaDeliveryIdResistArray,
    areaDeliveryCurrentPage,
    areaDeliveryTotalCount,
    isLoadingAreaDelivery,
    deliveryGroupCreateInput,
    deliveryGroupUpdateInput,
    deliveryGroupDeleteInput,
    use_list,
  };
};

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useDeliverySettingActions = ({
  overlay,
  errdialog,
  errMessage,
  processFlag,
  mode,
  displayInputDialog,
  displayConfirmDialog,
  displayCompletedDialog,
  areaDeliveryIdResistDialog,
  completeFlg,
  timeFormRef,
  groupFormRef,
  chainSelectValues,
  shopSelectValues,
  areaSelectValues,
  areaEditSelectValues,
  areaListItems,
  newAreaListItems,
  shopListItems,
  deliveryGroupListItems,
  deliveryGroupSelectValues,
  bufDeliveryGroupSelectValues,
  listDataArray,
  listGroupDeliveryIdDataArray,
  mappedListData,
  deliveryTimeCommonInput,
  deliveryTimeDeleteInput,
  deliveryGroupCreateInput,
  deliveryGroupUpdateInput,
  deliveryGroupDeleteInput,
  deliveryTimeGetInput,
  deliveryTimeListInput,
  deliveryTimeAllListInput,
  deliveryGroupListInput,
  deliveryGroupGetInput,
  deliverySelectValues,
  displayDaysSettingValues,
  selectTimeCapSelectValues,
  selectGroupCapSelectValues,
  selectTypeSelectValues,
  selectListCheckbox,
  listDeliveryTimeAll,
  listDataAreaDeliveryIdResistArray,
  listGroupDeliveryId,
  areaDeliveryCurrentPage,
  areaDeliveryTotalCount,
  isLoadingAreaDelivery,
  queryDeliverytime,
  onChangeShop,
  onChangeArea,
  onChangeGroup,
  syncTimeFormValues,
  syncGroupFormValues,
  createDeliveryTime,
  createDeliveryGroup,
  chkDeliveryId,
  chkDeliveryIdType,
  chkGroupDeliveryId,
  getDelivery,
  getAreaName,
  getShopName,
  getDeliveryIdName,
  use_list,
  wordList,
}: UseDeliverySettingActionsParams) => {
  /** ページサイズ定数 */
  const PAGE_SIZE = 50;

  /**
   * 便を取得
   */
  const getDeliveryId = (delivery_name: string): string => {
    let delivery_id: number | null = null;
    DELIVERY_ITEMS.forEach((value) => {
      if (value.label === delivery_name) {
        delivery_id = value.delivery_id;
      }
    });
    return delivery_id !== null ? String(delivery_id) : "0";
  };

  /**
   * エリア・便一覧のページ指定取得（共通処理）
   */
  const fetchAreaDeliveryPage = async (page: number): Promise<void> => {
    const apolloClient = await generateApolloClient();
    isLoadingAreaDelivery.value = true;

    // API呼び出し前に旧データをクリア（一瞬旧データが表示される問題の防止）
    listDataAreaDeliveryIdResistArray.value = [];

    try {
      // offset計算
      deliveryTimeAllListInput.limit = PAGE_SIZE;
      deliveryTimeAllListInput.offset = (page - 1) * PAGE_SIZE;

      // 件数取得（初回 or ページ変更時）
      const countInput: DeliveryTimeAllCountInput = {
        customer_code: deliveryTimeAllListInput.customer_code,
        chain_code: deliveryTimeAllListInput.chain_code,
        shop_code: deliveryTimeAllListInput.shop_code || undefined,
      };
      const countResult = await apolloClient.query<
        GetDeliveryTimeAllCountQuery,
        GetDeliveryTimeAllCountQueryVariables
      >({
        query: GetDeliveryTimeAllCountDocument,
        variables: { input: countInput },
        fetchPolicy: "no-cache",
      });
      areaDeliveryTotalCount.value =
        countResult.data.getDeliveryTimeAllCount?.count ?? 0;

      // データ取得
      const deliveryTimeAll = await apolloClient.query<
        ListDeliveryTimeAllQuery,
        ListDeliveryTimeAllQueryVariables
      >({
        query: ListDeliveryTimeAllDocument,
        variables: { input: deliveryTimeAllListInput },
        fetchPolicy: "no-cache",
      });
      listDeliveryTimeAll.value = deliveryTimeAll.data.listDeliveryTimeAll!;

      // 一覧データ構築
      listDataAreaDeliveryIdResistArray.value = [];
      if (listDeliveryTimeAll.value.length > 0) {
        await getDelivery();

        const _shop_list: Record<string, number> = {};
        listDeliveryTimeAll.value.forEach((buf_value) => {
          if (buf_value !== null && buf_value !== undefined) {
            _shop_list[buf_value.shop_code] =
              (_shop_list[buf_value.shop_code] || 0) + 1;
          }
        });

        let prev_shop_code = "";
        let shop_cnt = 0;

        for (const buf_value of listDeliveryTimeAll.value) {
          let deliveryStartTimeAry: string[] = [];
          let deliveryEndTimeAry: string[] = [];

          if (buf_value !== undefined && buf_value !== null) {
            if (prev_shop_code === buf_value.shop_code) {
              shop_cnt = 0;
            } else {
              shop_cnt = _shop_list[buf_value.shop_code];
              prev_shop_code = buf_value.shop_code;
            }

            const use_key =
              buf_value.shop_code + buf_value.area_code + buf_value.delivery_id;
            deliveryStartTimeAry = buf_value.delivery_start_time.split(":");
            deliveryEndTimeAry = buf_value.delivery_end_time.split(":");
            let use_flg = false;

            if (
              use_list[use_key] &&
              selectListCheckbox.value !== undefined
            ) {
              use_flg = true;
              selectListCheckbox.value.forEach((_value) => {
                if (
                  buf_value &&
                  _value &&
                  _value.shop_code +
                    ":" +
                    _value.area_code +
                    ":" +
                    _value.delivery_id ===
                    buf_value.shop_code +
                      ":" +
                      buf_value.area_code +
                      ":" +
                      getDeliveryIdName(buf_value.delivery_id)
                ) {
                  use_flg = false;
                }
              });
            }

            listDataAreaDeliveryIdResistArray.value.push({
              id: buf_value.area_code + buf_value.delivery_id,
              shop_code: buf_value.shop_code,
              area_code: buf_value.area_code,
              delivery_id: getDeliveryIdName(buf_value.delivery_id),
              delivery_time:
                deliveryStartTimeAry[0].padStart(2, "0") +
                ":" +
                deliveryStartTimeAry[1].padStart(2, "0") +
                "~" +
                deliveryEndTimeAry[0].padStart(2, "0") +
                ":" +
                deliveryEndTimeAry[1].padStart(2, "0"),
              shop_name: getShopName(buf_value.shop_code),
              area_name: buf_value.area_name ?? "",
              shop_cnt,
              use_flg,
            });

            if (selectListCheckbox.value !== undefined) {
              selectListCheckbox.value.forEach((_value) => {
                if (
                  buf_value &&
                  _value &&
                  _value.shop_code +
                    ":" +
                    _value.area_code +
                    ":" +
                    _value.delivery_id ===
                    buf_value.shop_code +
                      ":" +
                      buf_value.area_code +
                      ":" +
                      getDeliveryIdName(buf_value.delivery_id)
                ) {
                  _value.shop_cnt = shop_cnt;
                  _value.use_flg = false;
                }
              });
            }
          }
        }
      }

      areaDeliveryCurrentPage.value = page;
    } finally {
      isLoadingAreaDelivery.value = false;
    }
  };

  /**
   * キャップ選択ボックス変更 @change
   */
  const onChangeCapSelect = (): void => {
    if (mode.value === "time") {
      selectTimeCapSelectValues.value.item =
        GeneralTypeList.cap_setting[selectTimeCapSelectValues.value.index].item;
    } else {
      selectGroupCapSelectValues.value.item =
        GeneralTypeList.cap_setting[selectGroupCapSelectValues.value.index].item;
      // キャップ設定切り替え時にvee-validateの内部値を同期
      syncGroupFormValues();
    }
  };

  /**
   * 新規ボタン @Click
   */
  const onClickNewRegister = async (): Promise<void> => {
    try {
      // 初期化処理
      await initializeInput();

      // 新規フラグをONにする
      processFlag.value = 0;

      // エリア登録ダイアログ 表示
      displayInputDialog.value = true;
      // モードに応じてフォーム値を同期
      if (mode.value === "time") {
        await syncTimeFormValues();
      } else if (mode.value === "group") {
        await syncGroupFormValues();
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エラーが発生しました。";
    }
  };

  /**
   * 編集ボタン @click
   */
  const onClickEdit = async (item: ListData): Promise<void> => {
    try {
      // 編集フラグをONにする
      processFlag.value = 1;

      // 初期化処理
      await initializeUpdate(item);

      // 配達時間編集ダイアログ 表示
      displayInputDialog.value = true;
      // モードに応じてフォーム値を同期
      if (mode.value === "time") {
        await syncTimeFormValues();
      } else if (mode.value === "group") {
        await syncGroupFormValues();
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エラーが発生しました。";
    }
  };

  /**
   * 削除ボタン @click
   */
  const onClickDelete = async (item: ListData): Promise<void> => {
    // 削除フラグをONにする
    processFlag.value = 2;

    // 初期化処理
    await initializeUpdate(item);

    // 配達時間削除確認ダイアログ 表示
    displayConfirmDialog.value = true;
  };

  /**
   * 初期化処理(新規)
   */
  const initializeInput = async (): Promise<void> => {
    if (mode.value === "time") {
      areaEditSelectValues.value = { area_code: "", area_name: "" };

      const areaList = Array.isArray(areaListItems.value)
        ? areaListItems.value
        : [];
      newAreaListItems.value = areaList.filter((value) => value != null);

      newAreaListItems.value.forEach((value) => {
        if (areaEditSelectValues.value.area_code === "" && value) {
          areaEditSelectValues.value = {
            area_code: value.area_code,
            area_name: value.area_name,
          };
        }
      });

      deliverySelectValues.value = { label: "１便", delivery_id: 1 };
      deliveryTimeCommonInput.delivery_id = 0;
      deliveryTimeCommonInput.delivery_start_time = "";
      deliveryTimeCommonInput.delivery_end_time = "";
      deliveryTimeCommonInput.max_deliverable_unit = null;
      deliveryTimeCommonInput.delivery_cap_mon = null;
      deliveryTimeCommonInput.delivery_cap_tue = null;
      deliveryTimeCommonInput.delivery_cap_wed = null;
      deliveryTimeCommonInput.delivery_cap_thu = null;
      deliveryTimeCommonInput.delivery_cap_fri = null;
      deliveryTimeCommonInput.delivery_cap_sat = null;
      deliveryTimeCommonInput.delivery_cap_sun = null;
      deliveryTimeCommonInput.display_days_setting = 0;
      displayDaysSettingValues.value = { label: "当日", display_days_setting: 0 };
    } else if (mode.value === "group") {
      bufDeliveryGroupSelectValues.value = deliveryGroupSelectValues.value;
      deliveryGroupSelectValues.value = { group_code: "", name: "" };

      const groupList = Array.isArray(deliveryGroupListItems.value)
        ? deliveryGroupListItems.value
        : [];
      groupList.forEach((value) => {
        if (value && deliveryGroupSelectValues.value.group_code === "") {
          deliveryGroupSelectValues.value = {
            group_code: value.group_code,
            name: value.name,
          };
        }
      });

      deliverySelectValues.value = { label: "１便", delivery_id: 1 };
      deliveryGroupCreateInput.max_deliverable_unit = null;
      deliveryGroupCreateInput.group_cap_mon = null;
      deliveryGroupCreateInput.group_cap_tue = null;
      deliveryGroupCreateInput.group_cap_wed = null;
      deliveryGroupCreateInput.group_cap_thu = null;
      deliveryGroupCreateInput.group_cap_fri = null;
      deliveryGroupCreateInput.group_cap_sat = null;
      deliveryGroupCreateInput.group_cap_sun = null;
      deliveryGroupCreateInput.name = "";
      selectListCheckbox.value = [];
      selectTypeSelectValues.value = { index: "0", item: "新規" };
      selectTimeCapSelectValues.value = { index: 0, item: "全曜日" };
      selectGroupCapSelectValues.value = { index: 0, item: "全曜日" };

      // すべての店舗・エリアの配達時間を取得
      deliveryTimeAllListInput.chain_code = chainSelectValues.value.chain_code;
      const apolloClient = await generateApolloClient();
      const deliveryTimeAll = await apolloClient.query<ListDeliveryTimeAllQuery, ListDeliveryTimeAllQueryVariables>({
        query: ListDeliveryTimeAllDocument,
        variables: { input: deliveryTimeAllListInput },
        fetchPolicy: "no-cache",
      });
      listDeliveryTimeAll.value = deliveryTimeAll.data.listDeliveryTimeAll!;
      listDataAreaDeliveryIdResistArray.value = [];

      if (listDeliveryTimeAll.value.length > 0) {
        await getDelivery();
      }
    }
  };

  /**
   * 初期化処理(変更・削除)
   */
  const initializeUpdate = async (item: ListData): Promise<void> => {
    // graphql接続
    const apolloClient = await generateApolloClient();
    if (mode.value === "time") {
      for (var key in DELIVERY_ITEMS) {
        if (DELIVERY_ITEMS[key].delivery_id == Number(item.delivery_id)) {
          deliverySelectValues.value = {
            label: DELIVERY_ITEMS[key].label,
            delivery_id: Number(item.delivery_id),
          };
          break;
        }
      }

      for (var settingkey in DISPLAY_DAYS_SETTING_ITEMS) {
        if (
          DISPLAY_DAYS_SETTING_ITEMS[settingkey].label ==
          item.display_days_setting
        ) {
          displayDaysSettingValues.value = {
            label: DISPLAY_DAYS_SETTING_ITEMS[settingkey].label,
            display_days_setting:
              DISPLAY_DAYS_SETTING_ITEMS[settingkey].display_days_setting,
          };
          break;
        }
      }

      for (var deliveryidkey in DELIVERY_ITEMS) {
        if (DELIVERY_ITEMS[deliveryidkey].label == item.delivery_id) {
          deliverySelectValues.value = {
            label: DELIVERY_ITEMS[deliveryidkey].label,
            delivery_id: DELIVERY_ITEMS[deliveryidkey].delivery_id,
          };
          break;
        }
      }

      const areaList = Array.isArray(areaListItems.value)
        ? areaListItems.value
        : [];
      areaList.forEach((value) => {
        if (value!.area_code == item.area_code) {
          areaEditSelectValues.value = {
            area_code: value!.area_code,
            area_name: value!.area_name,
          };
        }
      });

      deliveryTimeCommonInput.delivery_id =
        deliverySelectValues.value.delivery_id;
      deliveryTimeCommonInput.delivery_start_time =
        item.delivery_start_time;
      deliveryTimeCommonInput.delivery_end_time = item.delivery_end_time;
      deliveryTimeCommonInput.max_deliverable_unit =
        item.max_deliverable_unit;
      deliveryTimeCommonInput.delivery_cap_mon = item.delivery_cap_mon;
      deliveryTimeCommonInput.delivery_cap_tue = item.delivery_cap_tue;
      deliveryTimeCommonInput.delivery_cap_wed = item.delivery_cap_wed;
      deliveryTimeCommonInput.delivery_cap_thu = item.delivery_cap_thu;
      deliveryTimeCommonInput.delivery_cap_fri = item.delivery_cap_fri;
      deliveryTimeCommonInput.delivery_cap_sat = item.delivery_cap_sat;
      deliveryTimeCommonInput.delivery_cap_sun = item.delivery_cap_sun;

      if (deliveryTimeCommonInput.max_deliverable_unit == null) {
        selectTimeCapSelectValues.value = {
          index: 1,
          item: "曜日ごと",
        };
      } else {
        selectTimeCapSelectValues.value = {
          index: 0,
          item: "全曜日",
        };
      }

      deliveryTimeCommonInput.display_days_setting =
        displayDaysSettingValues.value.display_days_setting;
      deliveryTimeCommonInput.area_code = item.area_code;
    } else if (mode.value === "group") {
      // グループ便詳細取得
      deliveryGroupGetInput.chain_code = chainSelectValues.value.chain_code;
      deliveryGroupGetInput.group_code = item.group_code;
      deliveryGroupGetInput.group_delivery_id = item.group_delivery_id;

      for (var _key in GROUP_DELIVERY_ITEMS) {
        if (
          GROUP_DELIVERY_ITEMS[_key].delivery_id == item.group_delivery_id
        ) {
          deliverySelectValues.value = {
            label: GROUP_DELIVERY_ITEMS[_key].label,
            delivery_id: item.group_delivery_id,
          };
          break;
        }
      }

      try {
        const result = await apolloClient.query({
          query: (await import("@/generated/graphql")).GetDeliveryGroupDocument,
          variables: { input: deliveryGroupGetInput },
          fetchPolicy: "no-cache",
        });

        if (
          result.data.getDeliveryGroup &&
          result.data.getDeliveryGroup.response &&
          result.data.getDeliveryGroup.response.length > 0
        ) {
          listGroupDeliveryId.value =
            result.data.getDeliveryGroup.response;
        }

        selectListCheckbox.value = [];
        let deliveryStartTimeAry: string[];
        let deliveryEndTimeAry: string[];

        if (
          listGroupDeliveryId.value &&
          listGroupDeliveryId.value[0] &&
          listGroupDeliveryId.value[0]!.assigned_id_list
        ) {
          listGroupDeliveryId.value[0]!.assigned_id_list!.forEach(
            async (value) => {
              areaListItems.value = [];
              // エリアデータ取得処理
              const listArea = await apolloClient.query<
                ListDeliveryAreaQuery,
                ListDeliveryAreaQueryVariables
              >({
                query: ListDeliveryAreaDocument,
                variables: {
                  chain_code: chainSelectValues.value.chain_code,
                  customer_code: localStorageGetCustomerCode(),
                  shop_code: value.shop_code,
                },
                fetchPolicy: "no-cache",
              });
              areaListItems.value = listArea.data.listDeliveryArea;
              const area_name = getAreaName(value.area_code);

              // 配達時間取得
              deliveryTimeGetInput.chain_code = chainSelectValues.value.chain_code;
              deliveryTimeGetInput.customer_code = localStorageGetCustomerCode();
              deliveryTimeGetInput.shop_code = value.shop_code;
              deliveryTimeGetInput.area_code = value.area_code;
              deliveryTimeGetInput.delivery_id = value.delivery_id;

              let delivery_time = "";
              try {
                const timeRes = await apolloClient.query<
                  GetDeliveryTimeQuery,
                  GetDeliveryTimeQueryVariables
                >({
                  query: GetDeliveryTimeDocument,
                  variables: { input: deliveryTimeGetInput },
                  fetchPolicy: "no-cache",
                });

                const deliveryTimeData = timeRes.data.getDeliveryTime;
                if (deliveryTimeData && deliveryTimeData.delivery_start_time && deliveryTimeData.delivery_end_time) {
                  deliveryStartTimeAry = deliveryTimeData.delivery_start_time.split(":");
                  deliveryEndTimeAry = deliveryTimeData.delivery_end_time.split(":");
                  delivery_time =
                    deliveryStartTimeAry[0].padStart(2, "0") +
                    ":" +
                    deliveryStartTimeAry[1].padStart(2, "0") +
                    "~" +
                    deliveryEndTimeAry[0].padStart(2, "0") +
                    ":" +
                    deliveryEndTimeAry[1].padStart(2, "0");
                }
              } catch (err) {
                // 配達時間取得に失敗した場合は空文字を使用
                console.warn("配達時間の取得に失敗しました:", err);
              }

              // 配達時間の取得結果に関わらず、selectListCheckboxに追加
              selectListCheckbox.value.push({
                id: value.area_code + value.delivery_id,
                shop_code: value.shop_code,
                area_code: value.area_code,
                delivery_id: getDeliveryIdName(value.delivery_id),
                delivery_time,
                shop_name: getShopName(value.shop_code),
                area_name,
                shop_cnt: 0,
                use_flg: false,
              });
            }
          );
        }

        // すべての店舗・エリアの配達時間を取得
        deliveryTimeAllListInput.chain_code = chainSelectValues.value.chain_code;
        const deliveryTimeAll = await apolloClient.query<ListDeliveryTimeAllQuery, ListDeliveryTimeAllQueryVariables>({
          query: ListDeliveryTimeAllDocument,
          variables: { input: deliveryTimeAllListInput },
          fetchPolicy: "no-cache",
        });
        listDeliveryTimeAll.value = deliveryTimeAll.data.listDeliveryTimeAll!;
        listDataAreaDeliveryIdResistArray.value = [];
        if (listDeliveryTimeAll.value.length > 0) {
          await getDelivery();
        }
      } catch (err: unknown) {
        console.error(err);
        return;
      }

      // 更新用 input 設定
      deliveryGroupUpdateInput.name = item.name;
      deliveryGroupUpdateInput.group_code = item.group_code;
      deliveryGroupUpdateInput.chain_code = chainSelectValues.value.chain_code;
      deliveryGroupUpdateInput.group_delivery_id = item.group_delivery_id;
      deliveryGroupUpdateInput.max_deliverable_unit = item.max_deliverable_unit;
      deliveryGroupUpdateInput.group_cap_mon = item.group_cap_mon;
      deliveryGroupUpdateInput.group_cap_tue = item.group_cap_tue;
      deliveryGroupUpdateInput.group_cap_wed = item.group_cap_wed;
      deliveryGroupUpdateInput.group_cap_thu = item.group_cap_thu;
      deliveryGroupUpdateInput.group_cap_fri = item.group_cap_fri;
      deliveryGroupUpdateInput.group_cap_sat = item.group_cap_sat;
      deliveryGroupUpdateInput.group_cap_sun = item.group_cap_sun;

      // 新規用 input を更新
      deliveryGroupCreateInput.name = deliveryGroupUpdateInput.name;
      deliveryGroupCreateInput.max_deliverable_unit = deliveryGroupUpdateInput.max_deliverable_unit;
      deliveryGroupCreateInput.group_cap_mon = deliveryGroupUpdateInput.group_cap_mon;
      deliveryGroupCreateInput.group_cap_tue = deliveryGroupUpdateInput.group_cap_tue;
      deliveryGroupCreateInput.group_cap_wed = deliveryGroupUpdateInput.group_cap_wed;
      deliveryGroupCreateInput.group_cap_thu = deliveryGroupUpdateInput.group_cap_thu;
      deliveryGroupCreateInput.group_cap_fri = deliveryGroupUpdateInput.group_cap_fri;
      deliveryGroupCreateInput.group_cap_sat = deliveryGroupUpdateInput.group_cap_sat;
      deliveryGroupCreateInput.group_cap_sun = deliveryGroupUpdateInput.group_cap_sun;
      if (deliveryGroupCreateInput.max_deliverable_unit == null) {
        selectGroupCapSelectValues.value = { index: 1, item: "曜日ごと" };
      } else {
        selectGroupCapSelectValues.value = { index: 0, item: "全曜日" };
      }

      if (processFlag.value === 2) {
        deliveryGroupDeleteInput.chain_code = chainSelectValues.value.chain_code;
        deliveryGroupDeleteInput.group_code = item.group_code;
        deliveryGroupDeleteInput.group_delivery_id =
          item.group_delivery_id;
      }
    }
  };

  /**
   * 配達便フォーカスアウト @blur
   */
  const onBlurDeliveryId = async (): Promise<void> => {
    overlay.value = true;

    // 配達便存在チェック
    if (processFlag.value === 0) {
      if (mode.value === "time") {
        if (deliverySelectValues.value.delivery_id === 0) {
          await chkDeliveryIdType();
          await chkDeliveryId();
        }
      } else if (mode.value === "group") {
        if (selectTypeSelectValues.value.index !== "0") {
          await chkGroupDeliveryId();
        }
      }
    }

    overlay.value = false;
  };

  /**
   * 入力キャンセルボタン @Click
   */
  const onClickInputCancel = async (): Promise<void> => {
    // 配達時間登録ダイアログ 非表示
    displayInputDialog.value = false;

    //新規戻りの場合グループ選択をリセット
    if (processFlag.value === 0) {
      deliveryGroupSelectValues.value = bufDeliveryGroupSelectValues.value;
    }
  };

  /**
   * 「登録手続きへ(確認)」ボタン @Click
   * 「配達グループを登録」ボタン @Click
   */
  const onClickOpenConfirmDialog = async (
    validateForm?: () => Promise<{ valid: boolean }>
  ): Promise<void> => {
    if (mode.value === "time") {
      if (validateForm) {
        const res = await validateForm();
        if (!res.valid) {
          return;
        }
      }
      if (
        deliveryTimeCommonInput.delivery_start_time >
        deliveryTimeCommonInput.delivery_end_time
      ) {
        errdialog.value = true;
        errMessage.value =
          "配達時間の開始時間は、終了時間以前の時刻を入力してください。";
        return;
      }
      if (processFlag.value === 0) {
        //配達便・都度便存在チェック
        const resType = await chkDeliveryIdType();
        if (!resType) {
          return;
        }
        //配達便存在チェック
        const res = await chkDeliveryId();
        if (!res) {
          return;
        }
      }
    } else if (mode.value === "group") {
      // グループモードでもバリデーションを実行
      if (validateForm) {
        const res = await validateForm();
        if (!res.valid) {
          return;
        }
      }
      let errFlg = 0;
      if (processFlag.value === 0) {
        //エリア・便選択チェック
        if (selectListCheckbox.value.length === 0) {
          errdialog.value = true;
          errMessage.value = "対象エリア・便が選択されていません。";
          errFlg = 1;
        } else {
          errFlg = 0;
        }
        if (errFlg === 1) {
          return;
        }
        if (selectTypeSelectValues.value.index !== "0") {
          //グループ便存在チェック
          const res = await chkGroupDeliveryId();
          if (!res) {
            return;
          }
          //グループが既存の場合グループコードとグループ名セットする
          deliveryGroupCreateInput.name = deliveryGroupSelectValues.value.name;
          deliveryGroupCreateInput.group_code = deliveryGroupSelectValues.value.group_code;
        }
      } else if (processFlag.value === 1) {
        deliveryGroupCreateInput.name = deliveryGroupUpdateInput.name;
      }
    }

    // 配達時間登録ダイアログ 非表示
    displayInputDialog.value = false;
    // 配達時間登録確認ダイアログ 表示
    displayConfirmDialog.value = true;
  };

  /**
   * 「エリア・便追加」ボタン @Click
   * 店舗選択が前提のため、モーダル表示時はデータ取得を行わない
   */
  const onClickAreaDeliveyIdResist = async (): Promise<void> => {
    areaDeliveryIdResistDialog.value = true;

    // chain_code設定
    deliveryTimeAllListInput.chain_code = chainSelectValues.value.chain_code;

    // 店舗が既に選択されている場合のみデータ取得
    if (shopSelectValues.value && shopSelectValues.value.shop_code) {
      deliveryTimeAllListInput.shop_code = shopSelectValues.value.shop_code;
      try {
        await fetchAreaDeliveryPage(1);
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error(err.message);
        } else {
          console.error("未知のエラー:", err);
        }
        return;
      }
    } else {
      // 店舗未選択時は一覧をクリア
      listDeliveryTimeAll.value = [];
      listDataAreaDeliveryIdResistArray.value = [];
      areaDeliveryTotalCount.value = 0;
      areaDeliveryCurrentPage.value = 1;
    }
  };

  /**
   * ページ切替 エリア・便追加モーダル
   */
  const onPageChangeAreaDeliveyIdResist = async (page: number): Promise<void> => {
    try {
      await fetchAreaDeliveryPage(page);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  /**
   * 「エリア・便追加」閉じる @Click
   */
  const onClickAreaDeliveyIdCancel = (): void => {
    // エリア・便追加ダイアログ 非表示
    areaDeliveryIdResistDialog.value = false;
  };

  /**
   * 配達時間の登録する @Click
   */
  const onClickConfirmEntry = async (): Promise<void> => {
    overlay.value = true;

    try {
      if (mode.value === "time") {
        // 配達便セット
        deliveryTimeCommonInput.delivery_id = deliverySelectValues.value.delivery_id;
        // 表示設定セット
        deliveryTimeCommonInput.display_days_setting = displayDaysSettingValues.value.display_days_setting;
        deliveryTimeCommonInput.area_code = areaEditSelectValues.value.area_code;
        // 配達時間作成
        await createDeliveryTime();
      } else if (mode.value === "group") {
        // selectListCheckboxのコピーを作成してGraphQL用に加工（元データを保持するため）
        const assignedIdListForMutation = selectListCheckbox.value.map((value) => {
          if (value !== null && value !== undefined) {
            return {
              shop_code: value.shop_code,
              area_code: value.area_code,
              delivery_id: getDeliveryId(value.delivery_id ?? ""),
            };
          }
          return value;
        });

        // createセット
        deliveryGroupCreateInput.assigned_id_list = assignedIdListForMutation;
        deliveryGroupCreateInput.chain_code = chainSelectValues.value.chain_code;
        deliveryGroupCreateInput.group_delivery_id = Number(deliverySelectValues.value.delivery_id);
        // updateセット
        if (processFlag.value === 1) {
          deliveryGroupUpdateInput.assigned_id_list = assignedIdListForMutation;
          if (selectGroupCapSelectValues.value.index === 0) {
            deliveryGroupUpdateInput.group_cap_mon = null;
            deliveryGroupUpdateInput.group_cap_tue = null;
            deliveryGroupUpdateInput.group_cap_wed = null;
            deliveryGroupUpdateInput.group_cap_thu = null;
            deliveryGroupUpdateInput.group_cap_fri = null;
            deliveryGroupUpdateInput.group_cap_sat = null;
            deliveryGroupUpdateInput.group_cap_sun = null;
          } else {
            deliveryGroupUpdateInput.max_deliverable_unit = null;
          }
        }

        // 配達グループ作成
        await createDeliveryGroup();
      }
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    }

    nextTick(async () => {
      // ローディング解除
      overlay.value = false;
      // 配達時間登録確認ダイアログ 非表示
      displayConfirmDialog.value = false;
      // 絞り込み検索 一覧再表示
      if (mode.value === "time") {
        if (areaSelectValues.value.area_code.length === 0) {
          await onChangeShop();
        } else {
          await onChangeArea();
        }
      } else {
        // 名前が変わっているかもしれないのでリストを取得しなおす
        const apolloClient = await generateApolloClient();
        deliveryGroupListInput.chain_code = chainSelectValues.value.chain_code;
        const listDeliveryGroup = await apolloClient.query<ListDeliveryGroupQuery, ListDeliveryGroupQueryVariables>({
          query: ListDeliveryGroupDocument,
          variables: { input: deliveryGroupListInput },
          fetchPolicy: "no-cache",
        });
        deliveryGroupListItems.value = listDeliveryGroup.data.listDeliveryGroup;

        if (processFlag.value === 2) {
          // 削除時: グループがまだリストに存在するか確認する
          const currentGroupCode = deliveryGroupSelectValues.value.group_code;
          const remainingGroup = deliveryGroupListItems.value.find(
            (item) => item?.group_code === currentGroupCode
          );
          if (remainingGroup) {
            // グループに他の設定が残っている場合、一覧を再表示する
            await onChangeGroup();
          } else {
            // グループの最後の設定が削除された場合、検索フィールドをクリアする
            deliveryGroupSelectValues.value = { group_code: "", name: "" };
            listGroupDeliveryIdDataArray.value = [];
          }
        } else if (processFlag.value === 1) {
          // 変更時: 最新のリストから該当グループを見つけて検索フィールドを同期する
          const currentGroupCode = deliveryGroupSelectValues.value.group_code;
          const updatedGroup = deliveryGroupListItems.value.find(
            (item) => item?.group_code === currentGroupCode
          );
          if (updatedGroup) {
            deliveryGroupSelectValues.value = {
              group_code: updatedGroup.group_code,
              name: updatedGroup.name,
            };
          }
          await onChangeGroup();
        } else {
          await onChangeGroup();
        }
      }
    });
  };

  /**
   * キャンセル @Click
   */
  const onClickConfirmCancel = async (): Promise<void> => {
    // 配達時間登録確認ダイアログ 非表示
    displayConfirmDialog.value = false;
    // 配達時間登録ダイアログ 表示
    if (processFlag.value !== 2) {
      displayInputDialog.value = true;
      if (mode.value === "time") {
        await syncTimeFormValues();
      } else if (mode.value === "group") {
        await syncGroupFormValues();
      }
    }
  };

  /**
   * 店舗選択ボックス エリア・便追加モーダル @change
   */
  const onChangeShopAreaDeliveyIdResist = async (): Promise<void> => {
    // chain_code 設定
    deliveryTimeAllListInput.chain_code = chainSelectValues.value.chain_code;
    // shop_code 設定
    if (shopSelectValues.value) {
      deliveryTimeAllListInput.shop_code = shopSelectValues.value.shop_code;
    } else {
      deliveryTimeAllListInput.shop_code = "";
      shopSelectValues.value = { shop_code: "", name: "" };
    }

    try {
      // 店舗切替時はページ1にリセット
      await fetchAreaDeliveryPage(1);
    } catch (err: unknown) {
      console.error(err);
      return;
    }
  };

  /**
   * 戻る @Click
   */
  const onClickCompletedGoStart = async (): Promise<void> => {
    // 配達時間登録完了ダイアログ 非表示
    displayCompletedDialog.value = false;

    await nextTick(async () => {
      // 絞り込み検索 一覧再表示
      if (areaSelectValues.value.area_code.length === 0) {
        await onChangeShop();
      } else {
        await onChangeArea();
      }
    });
  };

  return {
    getDeliveryId,
    onChangeCapSelect,
    onClickNewRegister,
    onClickEdit,
    onClickDelete,
    initializeInput,
    initializeUpdate,
    onBlurDeliveryId,
    onClickInputCancel,
    onClickOpenConfirmDialog,
    onClickAreaDeliveyIdResist,
    onClickAreaDeliveyIdCancel,
    onClickConfirmEntry,
    onClickConfirmCancel,
    onChangeShopAreaDeliveyIdResist,
    onPageChangeAreaDeliveyIdResist,
    onClickCompletedGoStart,
  };
};
